"use client";

import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import type { ContractAggregate } from "@/lib/sse/useSSE";
import { IncotermsPill, InsuranceSelectedLine } from "@/components/scenes/shared/IncotermsPill";
import { SignDocumentModal } from "@/components/sign/SignDocumentModal";
import type { SignerBlock } from "@/lib/sign/signerParties";
import { useUserActions } from "@/lib/userActions/UserActionsContext";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * ContractAndInsuranceSignModals — owns the Contract + Insurance sign
 * popups for the screen-2.1 / 4.5 / 5.5 beat. Lives one level above
 * <ContractCapture> so the modals survive the wrapper's swap to
 * <SplitScreen> when handshake #3 fires (otherwise the modals would
 * unmount within a few hundred ms of opening).
 *
 * Chain semantics: contract auto-opens once on mount; closing it queues
 * insurance to auto-open ONCE (via a ref, not a state flag — a state
 * flag would re-trigger the effect every time the insurance modal
 * closed, creating an infinite loop). After both have auto-opened, the
 * presenter can re-open either via the D hotkey.
 */
const DEFAULT_CONTRACT_SIGNER: SignerBlock = {
  signedAs:    "Aurora Aves Brasil S.A.",
  signerOrg:   "Seller (Exporter) · BR",
  actionLabel: "Sign as Aurora Aves Brasil S.A.",
};
const DEFAULT_INSURANCE_SIGNER: SignerBlock = {
  signedAs:    "Aurora Aves Brasil S.A.",
  signerOrg:   "Insured (Seller arranges under CIF) · BR",
  actionLabel: "Sign as Aurora Aves Brasil S.A.",
};
const DEFAULT_INSURANCE_REF = "ORB-INS-2026-0042 · 110% CIF · ICC(A)";

export function ContractAndInsuranceSignModals({
  contractRef,
  onChainComplete,
  contractSigner = DEFAULT_CONTRACT_SIGNER,
  insuranceSigner = DEFAULT_INSURANCE_SIGNER,
  contractPdf = "/sign-docs/contract.pdf",
  insurancePdf = "/sign-docs/insurance.pdf",
  insuranceRef = DEFAULT_INSURANCE_REF,
  requireCta = false,
  ctaActionKey,
}: {
  contractRef?: string;
  onChainComplete?: () => void;
  contractSigner?: SignerBlock;
  insuranceSigner?: SignerBlock;
  contractPdf?: string;
  insurancePdf?: string;
  insuranceRef?: string;
  /** When true, the contract popup does NOT auto-open on mount. A centered
   *  "Sign with your Orbitra ID" CTA is shown instead; clicking it kicks
   *  off the existing contract → insurance chain. Used on T2's 4.5. */
  requireCta?: boolean;
  /** Optional UserActions key. When set, the CTA "fired" state survives
   *  the SceneRouter unmount/remount on Shift+N replay — useUserActions
   *  persists the key to a session-scoped context. */
  ctaActionKey?: string;
}) {
  const userActions = useUserActions();
  const ctaAlreadyFired = ctaActionKey ? userActions.has(ctaActionKey) : false;
  // If the CTA has already been fired in this session, skip the gate
  // entirely on remount (e.g. step away from 4.5, step back, no second
  // wall). Otherwise fall back to the original `requireCta` semantics.
  const [ctaOpen, setCtaOpen] = useState(requireCta && !ctaAlreadyFired);
  const [contractOpen, setContractOpen] = useState(!requireCta || ctaAlreadyFired);
  const [insuranceOpen, setInsuranceOpen] = useState(false);
  // Preview-only modal that surfaces the sales-agreement PDF without
  // firing the sign chain. Used by the "View sales agreement" secondary
  // button on the CTA overlay so the audience can read the contract
  // before signing it.
  const [previewOpen, setPreviewOpen] = useState(false);
  const insuranceAutoFiredRef = useRef(false);
  const chainCompletedRef = useRef(false);

  const queueInsurance = useCallback(() => {
    if (insuranceAutoFiredRef.current) return;
    insuranceAutoFiredRef.current = true;
    setTimeout(() => setInsuranceOpen(true), 200);
  }, []);

  // Fires once — the moment the insurance modal closes for the first time,
  // which marks the end of the contract→insurance chain. Used by the
  // parent scene to defer the handshake #3 split-screen + the "Corp DID
  // signed" line until the human has interacted with both popups.
  const completeChainOnce = useCallback(() => {
    if (chainCompletedRef.current) return;
    chainCompletedRef.current = true;
    onChainComplete?.();
  }, [onChainComplete]);

  const t = useTranslations("contractCapture");

  return (
    <>
      {ctaOpen && typeof document !== "undefined" && createPortal(
        // Portal: escape .scene-fade so the CTA centres on the viewport.
        <div className="fixed inset-0 z-30 grid place-items-center bg-orbitra-ink/55 backdrop-blur-sm">
          <div className="glass w-[min(420px,90vw)] p-6 text-center">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("ctaKicker")}
            </p>
            <h3 className="mt-2 font-display text-base font-semibold text-orbitra-paper">
              {t("ctaTitle")}
            </h3>
            <p className="mt-2 text-[11px] text-orbitra-muted">
              {t("ctaBody")}
            </p>
            <button
              type="button"
              autoFocus
              onClick={() => {
                setCtaOpen(false);
                setContractOpen(true);
                if (ctaActionKey) userActions.fire(ctaActionKey);
              }}
              className="cta-primary mt-4 w-full px-5 py-2 font-display text-sm font-semibold"
            >
              {t("ctaSignButton")}
            </button>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="mt-2 w-full rounded border border-state-verified/40 px-5 py-2 font-mono text-[11px] uppercase tracking-wider text-state-verified hover:bg-state-verified/10"
            >
              {t("ctaViewButton")}
            </button>
          </div>
        </div>,
        document.body,
      )}
      <SignDocumentModal
        id="contract-preview"
        open={previewOpen}
        onOpen={() => setPreviewOpen(true)}
        onClose={() => setPreviewOpen(false)}
        pdfPath={contractPdf}
        documentTitle="International Sale & Purchase Agreement"
        documentRef={contractRef ?? "ORB-CTR-2026-0042"}
        signedAs={contractSigner.signedAs}
        signerOrg={contractSigner.signerOrg}
        viewOnly
        actionLabel="Close preview"
      />
      <SignDocumentModal
        id="contract"
        open={contractOpen}
        onOpen={() => setContractOpen(true)}
        onClose={() => {
          setContractOpen(false);
          queueInsurance();
        }}
        pdfPath={contractPdf}
        documentTitle="International Sale & Purchase Agreement"
        documentRef={contractRef ?? "ORB-CTR-2026-0042"}
        signedAs={contractSigner.signedAs}
        signerOrg={contractSigner.signerOrg}
        actionLabel={contractSigner.actionLabel}
      />
      <SignDocumentModal
        id="insurance"
        open={insuranceOpen}
        onOpen={() => setInsuranceOpen(true)}
        onClose={() => {
          setInsuranceOpen(false);
          completeChainOnce();
        }}
        pdfPath={insurancePdf}
        documentTitle="Marine Cargo Insurance Certificate"
        documentRef={insuranceRef}
        signedAs={insuranceSigner.signedAs}
        signerOrg={insuranceSigner.signerOrg}
        actionLabel={insuranceSigner.actionLabel}
      />
    </>
  );
}

/**
 * Screen 2.1 — Contract capture. Aurora's exporter view shows the
 * contract aggregate materialising on screen with a CDM badge.
 *
 *   Contract.id ORB-CTR-2026-0042 · originType Contract
 *   Incoterms CIF · HS 0207.12 · 28,000 kg · USD 850,000 · 21d
 *
 * Split-screen handshake #3 fires shortly after — Al Madar's importer
 * view briefly shows the contract appear in their pipeline as Buyer,
 * mirroring Aurora's Seller role. The handshake itself is rendered by
 * SceneRouter when it sees handshake number 3 in the projection.
 */
export function ContractCapture({
  contract,
  captured = false,
  corpDidSigned = true,
  onBeginLcComposition,
  lcCompositionStarted = false,
  className,
}: {
  contract: ContractAggregate | null;
  captured?: boolean;
  // When false, the "Both parties signed with their Corp DID" footer line
  // is hidden — used while the human-sign popup chain is still in flight.
  corpDidSigned?: boolean;
  // When set, a "Begin LC composition →" CTA renders at the bottom of the
  // contract card (once the Corp-DID-signed line appears). Click fires
  // the callback. Used on T2's 4.5 to kick off the LC composition
  // progress that follows through 4.12 + 4.13.
  onBeginLcComposition?: () => void;
  // Hides the begin-LC button once composition has started (callback
  // already fired — no second click).
  lcCompositionStarted?: boolean;
  className?: string;
}) {
  const c = contract ?? {
    id: "ORB-CTR-2026-0042",
    incoterms: "CIF",
    hsCode: "0207.12",
    commodity: "Frozen Halal Whole Chicken",
    quantityKg: 28_000,
    priceUsd: 850_000,
    deliveryWindowDays: 21,
  };

  const t = useTranslations("contractCapture");
  return (
    <div className={clsx("grid h-full grid-cols-3 gap-4", className)}>
      <div className="glass col-span-2 p-5">
        <div className="flex items-center justify-between border-b border-state-verified/15 pb-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("tradePipelineKicker")}</p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{t("captureTitle")}</h3>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span className={clsx(
              "rounded-full border px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider",
              captured
                ? "border-policy-pass/40 bg-policy-pass/10 text-policy-pass"
                : "border-state-verified/40 bg-state-verified/10 text-state-verified",
            )}>
              {captured ? t("chipCaptured") : t("chipReviewing")}
            </span>
            <IncotermsPill basis="CIF" who="Aurora arranges marine cargo insurance" verbose />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
          <Field label={t("fieldBuyer")}           value={t("buyerT1Value")} />
          <Field label={t("fieldSeller")}          value={t("sellerT1Value")} />
          <Field label={t("fieldCommodity")}       value={`${c.commodity} · HS ${c.hsCode}`} />
          <Field label={t("fieldQuantity")}        value={`${c.quantityKg?.toLocaleString()} kg`} />
          <Field label={t("fieldIncoterms")}       value={c.incoterms ?? "—"} />
          <Field label={t("fieldPrice")}           value={`USD ${c.priceUsd?.toLocaleString()}`} />
          <Field label={t("fieldDeliveryWindow")}  value={`${c.deliveryWindowDays} ${t("deliveryWindowSuffix")}`} />
          <Field label={t("fieldDestinationPort")} value={t("destinationPortT1")} />
        </div>

        {corpDidSigned && (
          <div className="mt-5 flex items-center gap-3 rounded-md border border-state-verified/30 bg-orbitra-ink/40 px-3 py-2 text-[11px]">
            <span className="font-mono text-state-verified">●</span>
            <span className="text-orbitra-paper">
              {t("bothPartiesSigned")}
            </span>
            <span className="ms-auto font-mono text-orbitra-teal-soft" dir="ltr">{c.id}</span>
          </div>
        )}

        <InsuranceSelectedLine
          className="mt-2"
          coverage="≥ 110% CIF"
          arrangedBy="Aurora"
        />

        {onBeginLcComposition && corpDidSigned && !lcCompositionStarted && (
          <button
            type="button"
            onClick={onBeginLcComposition}
            className="cta-primary mt-4 w-full py-2 text-[12px] font-semibold"
          >
            {t("beginLcCta")}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="glass p-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("contractRecordTitle")}</p>
          <p className="mt-2 text-[11px] text-orbitra-paper">{t("contractRecordSub")}</p>
          <div className="mt-1 rounded-md border border-state-verified/30 bg-orbitra-ink/60 p-2 text-[11px]">
            <p className="font-mono text-orbitra-paper">{t("contractRecordType")}</p>
            <p className="font-mono text-orbitra-paper" dir="ltr">id: {c.id}</p>
            <p className="font-mono text-orbitra-muted">{t("contractRecordOrigin")}</p>
          </div>
        </div>
        <div className="glass p-4 text-[11px]">
          <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("atlasAgentLabel")}</p>
          <p className="mt-2 text-orbitra-paper">"{t("atlasAgentQuote")}"</p>
        </div>
        {/* LC composition progress — moved here (under Atlas Agent) on T2 4.5
         *  per the latest amendment. Renders only when the parent declares
         *  the LC trigger is in scope (onBeginLcComposition provided). */}
        {onBeginLcComposition && (
          <div className="glass p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
              LC Composition · {lcCompositionStarted ? "In Progress" : "Pending"}
            </p>
            <p className="mt-2 text-[11px] text-orbitra-paper">
              {lcCompositionStarted
                ? "Contract + insurance signed. Composing the LC fields — Aurora and Al Madar can review as they materialise."
                : "Sign the contract + insurance to begin composing the Letter of Credit."}
            </p>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-orbitra-ink/60">
              <div
                className="h-full bg-state-financed transition-[width] duration-700 ease-state"
                style={{ width: lcCompositionStarted ? "15%" : "0%" }}
              />
            </div>
            <p className="mt-1 text-[10px] text-orbitra-muted">
              {lcCompositionStarted ? "15% complete" : "0% complete"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className="mt-0.5 text-orbitra-paper">{value}</p>
    </div>
  );
}
